import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import EnergyOverviewCarousel from '../components/energy-overview-slider/EnergyOverviewCarousel.tsx'
import EnergyOverviewSlide from '../components/energy-overview-slide/EnergyOverviewSlide.tsx'
import { formatAmount } from 'utils/format.ts'
import { getAmbition, getProvinceCode, getSelectedRegion } from '../utils.ts'
import { CommunityProps } from '../sections/types.ts'
import { BrandColors } from '../components/energy-overview-slide/types.ts'
import ThanksSlide from '../components/thanks-slide/ThanksSlide.tsx'
import RenewableSourceCard from '../components/renewable-source-card/RenewableSourceCard.tsx'
import { AMBITION_CHART_IMAGES } from '../sections/constants.ts'
import styles from '../sections/EnergyOverviewSection.module.scss'
import { City } from '../types.ts'
import SmallProducerCard from 'components/small-producer-card/SmallProducerCard.tsx'
import imageMiniGenerator from '/images/year-overview/mini-generator.png'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { ProducerEnergyType } from 'types/producer.ts'
import { Card } from '@boltenergy-be/design-system'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { Region, REGIONS } from '@boltenergy-be/shared'
import { Province, PROVINCES_WITH_CITIES } from '../constants.ts'

const SectionCommunity = ({ data }: CommunityProps) => {
  // REDUX STORE
  const { electricity } = useStoreSelector(selectCurrentServiceContracts)

  // i18n
  const { t } = useTranslation(['energyOverview', 'producer'])
  const language = useGetLanguage()

  // Local state
  const [city, setCity] = useState<City | null>(null)
  const [region, setRegion] = useState<Region | null>(null)
  const [province, setProvince] = useState<Province | null>(null)
  const [confetti, setConfetti] = useState<boolean>(false)

  // Constants
  const { households, producers, carbonDioxide, miniProducers } = data

  // Memo's
  const ambition = useMemo(
    () => getAmbition(households.people, region || REGIONS.flanders, province),
    [households.people, region, province]
  )
  const emissionBolt = useMemo(
    () => (city?.population ? Math.round(((producers.usage * 209) / 1000) * (households.people / city.population)) : null),
    [city?.population, households.people, producers.usage]
  )

  /**
   * Calculate which city to compare to
   **/
  useEffect(() => {
    // 1. Get postalCode
    const postalCode = electricity.deliveryPoint.address?.postalCode || '1000'

    // 2. Get/set province code
    const provinceCode = getProvinceCode(parseInt(postalCode as string))
    setProvince(provinceCode)

    // 3. Get/set region
    setRegion(getSelectedRegion(provinceCode, language, true))

    // 4. Get cities
    const filteredProvince = PROVINCES_WITH_CITIES.filter((province) => province.province === provinceCode)

    // 5. Compare households.people with population of cities
    const goal = households.people
    const beatenCity = filteredProvince[0]?.cities.filter((city) => city.population <= goal).sort((a, b) => b.population - a.population)[0]

    setCity(beatenCity)
  }, [electricity.deliveryPoint.address?.postalCode, households.people, language])

  return (
    <>
      <Card.Title>{t(($) => $.community.title)}</Card.Title>

      <EnergyOverviewCarousel id="community" triggerConfetti={(value) => setConfetti(value)}>
        {/* HOUSEHOLDS SLIDE */}
        {households?.total && (
          <EnergyOverviewSlide
            color={BrandColors.YELLOW}
            data={formatAmount(households.people, { fractionDigits: 0 })}
            label={t(($) => $.community.households.label)}
            description={t(($) => $.community.households.description, {
              amount: formatAmount(households.people, { fractionDigits: 0 }),
              city: city?.name[language] ?? ''
            })}
          />
        )}

        {/* CARBON DIOXIDE AVERAGE SLIDE */}
        {carbonDioxide?.reduction && emissionBolt && city && (
          <EnergyOverviewSlide
            color={BrandColors.BLUE}
            data={`-${formatAmount(carbonDioxide.reduction / 1000, { fractionDigits: 0 })}`}
            label={t(($) => $.community.carbonDioxideAverage.label)}
            metric={t(($) => $.community.carbonDioxideAverage.metric)}
            description={t(($) => $.community.carbonDioxideAverage.description, {
              amount: formatAmount(carbonDioxide.reduction / 1000, { fractionDigits: 0 })
            })}
          >
            <div className={classNames(styles.graph, styles['graph-emissions-community'])}>
              <div className={classNames(styles.bar, styles.community)}>
                <data
                  value={emissionBolt}
                  className={styles['with-bolt-icon']}
                  style={{
                    height: `${(emissionBolt / city.emission) * 100}%`
                  }}
                />
                <div className={styles.label}>Bolt</div>
              </div>

              {city && (
                <div key={province} className={styles.bar}>
                  <data value={city.emission} style={{ height: '100%' }} />
                  <div className={styles.label}>{city.name[language]}</div>
                </div>
              )}
            </div>
          </EnergyOverviewSlide>
        )}

        {/* PRODUCERS SLIDE */}
        {producers?.total && (
          <EnergyOverviewSlide
            color={BrandColors.PINK}
            data={formatAmount(producers.total, { fractionDigits: 0 })}
            label={t(($) => $.community.producers.label)}
            description={t(($) => $.community.producers.description, {
              amount: formatAmount(producers.total, { fractionDigits: 0 })
            })}
          >
            <div className={styles['renewable-sources-container']}>
              {producers.production?.solar && (
                <RenewableSourceCard
                  energyType={ProducerEnergyType.SOLAR}
                  tag={t(($) => $.energyType[ProducerEnergyType.SOLAR], { ns: 'producer' })}
                  pct={Math.round((producers.production.solar / producers.production.total) * 100)}
                  mwh={formatAmount(producers.production.solar / 1000, { fractionDigits: 0 })}
                />
              )}
              {producers.production?.wind && (
                <RenewableSourceCard
                  energyType={ProducerEnergyType.WIND}
                  tag={t(($) => $.energyType[ProducerEnergyType.WIND], { ns: 'producer' })}
                  pct={Math.round((producers.production.wind / producers.production.total) * 100)}
                  mwh={formatAmount(producers.production.wind / 1000, { fractionDigits: 0 })}
                />
              )}
              {producers.production?.water && (
                <RenewableSourceCard
                  energyType={ProducerEnergyType.WATER}
                  tag={t(($) => $.energyType[ProducerEnergyType.WATER], { ns: 'producer' })}
                  pct={Math.round((producers.production.water / producers.production.total) * 100)}
                  mwh={formatAmount(producers.production.water / 1000, { fractionDigits: 0 })}
                />
              )}
              {producers.production?.bio && (
                <RenewableSourceCard
                  energyType={ProducerEnergyType.BIO}
                  tag={t(($) => $.energyType[ProducerEnergyType.BIO], { ns: 'producer' })}
                  pct={Math.round((producers.production.bio / producers.production.total) * 100)}
                  mwh={formatAmount(producers.production.bio / 1000, { fractionDigits: 0 })}
                />
              )}
            </div>
          </EnergyOverviewSlide>
        )}

        {/* BOLT AMBITION SLIDE */}
        {ambition && (
          <EnergyOverviewSlide
            color={BrandColors.YELLOW}
            data={formatAmount(ambition.goal, { fractionDigits: 0 })}
            label={t(($) => $.community.ambition.label)}
            description={t(($) => $.community.ambition.description, {
              now: ambition.beaten[ambition.beaten.length - 1].name[language],
              soon: ambition.upcoming[0].name[language]
            })}
          >
            <div className={styles['graph-ambition-container']}>
              <img src={AMBITION_CHART_IMAGES[ambition.beaten.length]} alt={t(($) => $.community.ambition.imageAlt)} />
              <ul className={styles.cities}>
                {ambition.beaten.map((city) => (
                  <li key={city.name[language]}>{city.name[language]}</li>
                ))}
                <li className={styles.bolt}>Bolt</li>
                {ambition.upcoming.map((city) => (
                  <li key={city.name[language]}>{city.name[language]}</li>
                ))}
              </ul>
            </div>
          </EnergyOverviewSlide>
        )}

        {/* MINI GENERATORS SLIDE */}
        {miniProducers?.amount && (
          <EnergyOverviewSlide
            color={BrandColors.ORANGE}
            data={formatAmount(miniProducers.amount, { fractionDigits: 0 })}
            label={t(($) => $.community.miniGenerators.label)}
            description={t(($) => $.community.miniGenerators.description, {
              households: formatAmount(miniProducers.households, { fractionDigits: 0 })
            })}
          >
            <img src={imageMiniGenerator} alt={t(($) => $.personal.miniGenerators.imageAlt)} />
            <SmallProducerCard
              className={styles['small-card']}
              energyType={ProducerEnergyType.SOLAR}
              location={t(($) => $.community.miniGenerators.location)}
            />
          </EnergyOverviewSlide>
        )}

        {/* THANKS SLIDE */}
        <ThanksSlide popConfetti={confetti} color="blue" text={t(($) => $.community.thanks.text)} />
      </EnergyOverviewCarousel>
    </>
  )
}

export default SectionCommunity
